const STORAGE_KEY = 'levayo:recent-products:v1';
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;

class LevayoRecentlyViewed extends HTMLElement {
  async connectedCallback() {
    const now = Date.now();
    let entries = [];
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (Array.isArray(stored)) entries = stored.filter((entry) => this.valid(entry) && now - entry.seen < MAX_AGE);
    } catch { /* Storage can be unavailable in private browsing. */ }
    const currentData = this.querySelector('[data-current-product]');
    try {
      const current = JSON.parse(currentData?.textContent || 'null');
      if (current) {
        current.seen = now;
        if (this.valid(current)) entries = [current, ...entries.filter((entry) => entry.id !== current.id)];
      }
      entries = entries.slice(0, 20);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch { /* Render the available in-memory history without storage. */ }
    this.controller?.abort();
    this.controller = new AbortController();
    if (this.dataset.cardSection) {
      await this.renderSectionCards(entries);
      return;
    }
    const root = this.dataset.root.endsWith('/') ? this.dataset.root : `${this.dataset.root}/`;
    // Refresh prices and availability; removed/unpublished products are omitted.
    const products = await Promise.all(entries.slice(0, 5).map(async (entry) => {
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(entry.handle)}.js`, { signal: this.controller.signal });
        if (!response.ok) return null;
        const product = await response.json();
        const priceVariant = product.variants?.find((variant) => variant.price === product.price);
        return { ...entry, title: product.title, price: product.price, priceVaries: product.price_varies,
          compareAtPrice: priceVariant ? priceVariant.compare_at_price : product.compare_at_price,
          url: `${root}products/${encodeURIComponent(entry.handle)}`, image: product.featured_image };
      } catch { return null; }
    }));
    if (!this.isConnected) return;
    this.render(products.filter(Boolean));
  }
  valid(entry) {
    return entry && typeof entry.handle === 'string' && /^[a-z0-9-]+$/.test(entry.handle) &&
      typeof entry.title === 'string' && Number.isFinite(entry.price) && Number.isFinite(entry.seen);
  }
  async renderSectionCards(entries) {
    const controller = this.controller;
    const root = this.dataset.root || '/';
    const limit = Math.max(1, Math.min(12, Number(this.dataset.limit) || 6));
    const unique = entries.filter((entry, index, all) => all.findIndex(other => other.handle === entry.handle) === index).slice(0, limit);
    const cards = await Promise.all(unique.map(async entry => {
      try {
        const url = new URL(`${root.endsWith('/') ? root : root + '/'}products/${encodeURIComponent(entry.handle)}`, location.origin);
        url.searchParams.set('section_id', this.dataset.cardSection);
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) return null;
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const holder = documentFragment.querySelector('[data-recent-product-card]');
        if (holder?.dataset.recentProductCard !== entry.handle) return null;
        const card = holder.querySelector('.lvh-card');
        return card ? document.importNode(card, true) : null;
      } catch { return null; }
    }));
    if (!this.isConnected || this.controller !== controller || controller.signal.aborted) return;
    const grid = this.querySelector('.lvhd__grid');
    if (!grid) return;
    const items = cards.filter(Boolean).map(card => {
      const item = document.createElement('li');
      item.append(card);
      return item;
    });
    grid.replaceChildren(...items);
    this.hidden = items.length === 0;
  }
  render(products) {
    const grid = this.querySelector('.lv-recent__grid');
    grid.replaceChildren();
    const money = new Intl.NumberFormat(this.dataset.locale || 'de', { style: 'currency', currency: this.dataset.currency || 'EUR' });
    for (const product of products) {
      const card = document.createElement('article');
      card.className = 'lv-recent__card';
      card.setAttribute('role', 'listitem');
      const link = document.createElement('a');
      link.className = 'lv-recent__link';
      link.href = product.url;
      if (product.image) {
        const media = document.createElement('div');
        media.className = 'lv-recent__media';
        const image = document.createElement('img');
        image.className = 'lv-recent__image';
        const url = new URL(product.image, location.origin);
        url.searchParams.set('width', '600');
        image.src = url.href;
        image.alt = product.title;
        image.width = 600;
        image.height = 600;
        image.loading = 'lazy';
        media.append(image);
        if (product.hotDeal === true) {
          const deal = document.createElement('span');
          deal.className = 'lv-recent__deal';
          deal.textContent = 'Hot Deal';
          media.append(deal);
        }
        link.append(media);
      }
      const title = document.createElement('h3');
      title.className = 'lv-recent__title';
      title.textContent = product.title;
      const price = document.createElement('p');
      price.className = 'lv-recent__price';
      const discounted = Number.isFinite(product.compareAtPrice) && product.compareAtPrice > product.price;
      if (discounted) {
        const original = document.createElement('del');
        original.className = 'lv-recent__original';
        original.setAttribute('aria-label', `Ursprünglicher Preis: ${money.format(product.compareAtPrice / 100)}`);
        original.textContent = money.format(product.compareAtPrice / 100);
        price.append(original);
      }
      const current = document.createElement('span');
      current.textContent = `${product.priceVaries ? 'Ab ' : ''}${money.format(product.price / 100)}`;
      price.append(current);
      if (discounted) {
        const saving = document.createElement('span');
        saving.className = 'lv-recent__saving';
        saving.textContent = `−${Math.round((1 - product.price / product.compareAtPrice) * 100)} %`;
        price.append(saving);
      }
      link.append(title, price);
      card.append(link);
      grid.append(card);
    }
    this.hidden = products.length === 0;
  }
  disconnectedCallback() { this.controller?.abort(); }
}
if (!customElements.get('levayo-recently-viewed')) customElements.define('levayo-recently-viewed', LevayoRecentlyViewed);
